import React, { useCallback, useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { CanvasInfo } from '../../../model/BadgeModel';
import { Coordinate, GraphicActionType, GraphicPageMode, RedLine, RedlineValues, TextObj } from '../../../model/GraphicModel';
import { useStoreDispatch, useStoreSelector } from '../../../store';
import { setActionType, setDisableMenuOptsAction } from '../../../store/badge/actions';
import { disableMenuOptsAction, setGraphicActionType, switchFloatingWindowVisibilityAction } from '../../../store/graphic/actions';
import {
	isAlarmQueueOpenedSelector,
	isBirdEyeOpenedSelector,
	isDevicesOpenedSelector,
	isObjectPropertiesOpenedSelector,
	isPlottedDeviceOpenedSelector,
	isQuickAccessOpenedSelector,
} from '../../../store/graphic/selectors';
import { DraggableWindow } from '../../common/DraggableWindow/DraggableWindow';
import { TextWindow, TextWindowFooter } from '../TextWindow';
import { allStatesImageDirectory } from '../helper';

export enum MenuItems {
	// MainOptions
	File = 1,
	Drawing,
	Selection,
	Text,
	// FileOptions
	Save,
	NewFile,
	ImportFile,
	Directory,
	ExportFile,
	Undo,
	Redo,
	Copy,
	Paste,
	Cut,
	Delete,
	PageSetup,
	// DrawingOptions
	RedlineFreeHand,
	RedlineRectangle,
	RedlineFilledRectangle,
	RedlineCircle,
	RedlineFilledCircle,
	RedlineArrow,
	DeleteRedline,
	MonitorZone,
	Pin,
	LineWeight,
	LineWeightLine,
	LineSelectColorConfig,
	FillSelectColorConfig,
	// SelectionOptions
	Select,
	Move,
	Group,
	Ungroup,
	Rotate,
	Resize,
	FlipVertical,
	FlipHorizontal,
	BringToFront,
	SendToBack,
	BringForward,
	SendBackward,
	// TextOptions
	AddText,
	AddParagraph,
	Bold,
	Italic,
	Underline,
	AlignLeft,
	AlignCenter,
	AlignRight,
	FontColor,
	SearchText,
	// ZoomBarOptions
	ZoomOut,
	ZoomIn,
	ExtendZoom,
	// View
	View,
	ViewDevices,
	ViewAlarmQueue,
	ViewPlottedDevices,
	ViewObjectProperties,
	ViewQuickAccess,
	ViewBirdEye,
}

type WithMenuActions = {
	disabled: boolean;
	handleMenuActions: (menuItem: MenuItems, evt?: React.MouseEvent<HTMLElement>) => Promise<boolean>;
} & Partial<CanvasInfo>;

const escapeOptionEventListener = 'hasEscapeOptionEventListener';

const iconHeight = 48;
const iconWidth = 64;

enum DraggingElementType {
	Paragraph = 1,
	Image,
}

const withMenuActions = <T extends {}>(WrappedComponent: React.ComponentType<T & WithMenuActions>) => {
	const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

	const ComponentWithMenuActions: React.FC<T & WithMenuActions> = props => {
		const isBirdEyeOpened = useStoreSelector(isBirdEyeOpenedSelector);
		const isDevicesOpened = useStoreSelector(isDevicesOpenedSelector);
		const isAlarmQueueOpened = useStoreSelector(isAlarmQueueOpenedSelector);
		const isObjectPropertiesOpened = useStoreSelector(isObjectPropertiesOpenedSelector);
		const isPlottedDeviceOpened = useStoreSelector(isPlottedDeviceOpenedSelector);
		const isQuickAccessOpened = useStoreSelector(isQuickAccessOpenedSelector);

		const dispatch = useStoreDispatch();

		const [isTextWindowOpened, setIsTextWindowOpened] = useState<boolean>(false);
		const [isStickyNote, setIsStickyNote] = useState<boolean>(false);
		const [currentDraggingElementType, setCurrentDraggingElementType] = useState<DraggingElementType>(DraggingElementType.Image);
		const [currentTextObj, setCurrentTextObj] = useState<TextObj>();
		const [canvasMainContainerCoordinates, setCanvasMainContainerCoordinates] = useState<Coordinate>({
			x: 0,
			y: 0,
		});

		const canvasContainerElement = useRef<HTMLElement>();
		const canvasMainContainerElement = useRef<HTMLElement>();
		const draggingElement = useRef<HTMLElement>();

		const isDesignMode = props.viewMode === GraphicPageMode.Design;
		const loadTextWindow = isDesignMode && isTextWindowOpened;

		const canvasContainerId = props.containerId;
		const canvasMainContainerId = props.mainContainerId;
		const isMaps = props.isMaps;
		const graphicActionType = props.graphicActionType;
		const disabledOpts = props.disableOptions;

		useEffect(() => {
			canvasContainerElement.current = document.getElementById(canvasContainerId);
			canvasMainContainerElement.current = document.getElementById(canvasMainContainerId);
			return () => {
				localStorage.removeItem(escapeOptionEventListener);
				document.removeEventListener('keyup', releaseBlockingMenuItems);
			};
		}, []);

		useEffect(() => {
			canvasContainerElement.current.addEventListener('mouseup', dropHandler);
			return () => {
				canvasContainerElement.current.removeEventListener('mouseup', dropHandler);
			};
		}, [currentDraggingElementType, currentTextObj]);

		const disableMenuOptions = (value: boolean) => {
			if (isMaps) {
				dispatch(disableMenuOptsAction(value));
			} else {
				dispatch(setDisableMenuOptsAction(value));
			}
		};

		const releaseBlockingMenuItems = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				disableMenuOptions(false);
				localStorage.removeItem(escapeOptionEventListener);
				document.removeEventListener('keyup', releaseBlockingMenuItems);
			}
		};

		const addEscapeOptionEventListener = () => {
			const flag: string = localStorage.getItem(escapeOptionEventListener);
			if (!flag) {
				document.addEventListener('keyup', releaseBlockingMenuItems);
			}
		};

		const blockMenuItemsWithAddEscapeOptionEventListener = () => {
			disableMenuOptions(true);
			addEscapeOptionEventListener();
		};

		const draggingHandler = (evt: MouseEvent) => {
			evt.preventDefault();
			if (draggingElement.current) {
				const [left, top] = getPositions(evt.clientX, evt.clientY);
				draggingElement.current.style.left = `${left}px`;
				draggingElement.current.style.top = `${top}px`;
			}
		};

		const dropHandler = useCallback(
			(evt: MouseEvent) => {
				evt.preventDefault();
				if (draggingElement.current) {
					const { clientX, clientY } = evt;

					const uniqueId = getGuid();
					if (currentDraggingElementType === DraggingElementType.Image) {
						const objectTypeKey = 'Pin';
					} else {
						if (isStickyNote) {
						} else if (currentTextObj) {
							const currentRedlines = [];
							const { bold, fontColor, fontSize, italic, lineThrough, text, underline } = currentTextObj;
							const fontStyle: string = italic ? ' font-style: italic;' : '';
							const fontWeight: string = bold ? ' font-weight: bold;' : '';
							const textDecoration: string =
								underline || lineThrough ? ` text-decoration:${underline ? ' underline' : ''}${lineThrough ? ' line-through' : ''};` : '';
							const tempRedline: RedLine = {
								node: `RED_`,
								outerhtml: `<text
													x='0'
													y='0'
													fill='${fontColor}'
													style=''
													transform='matrix(${0.25 * fontSize},0,0,${0.25 * fontSize},--x,y--)'
													id='cv_RED_'>
														<tspan 
														style='${fontStyle}
															${fontWeight}
															${textDecoration}'>
															${text}
														</tspan>
												</text>`,
								name: `redline`,
								type: undefined,
								id: '',
								layer: 'RedLineLayer',
								group: 'RedLineLayer',
								color: fontColor,
								strokeWidth: '4',
								fill: fontColor,
								username: 'none',
								userid: 'none',
								currentPage: '1',
								triange_design: 'none',
								polypath_arrow: 'none',
								redline_text: text,
								redline_font_size: fontSize,
								fill_opacity: '0.01',
								transform: `T--x,y--`,
								drawingRotation: '0',
							};
							const tempRedlines: RedLine[] = [];
							tempRedline.id = uniqueId;
							tempRedline.name = `redline${uniqueId}`;
							tempRedline.node = `RED_${uniqueId}`;
							tempRedline.transform = tempRedline.transform.replace('--x,y--', `${clientX},${clientY}`);
							tempRedline.outerhtml = tempRedline.outerhtml.replace('--x,y--', `${clientX},${clientY}`);
							const redlinesObject: RedlineValues = {
								RedlineObjects: [...tempRedlines, tempRedline],
							};
						}
						batch(() => {
							setCurrentTextObj(undefined);
							disableMenuOptions(false);
						});
					}

					draggingElement.current.remove();
					draggingElement.current = null;
				}
				canvasContainerElement.current.removeEventListener('mousemove', draggingHandler);
				canvasContainerElement.current.removeEventListener('mouseup', dropHandler);
			},
			[currentDraggingElementType, currentTextObj]
		);

		const getPositions = (clientX: number, clientY: number): number[] => {
			const rect = canvasContainerElement.current.getBoundingClientRect();
			const left = clientX - rect.left;
			const top = clientY - rect.top;
			return [left, top];
		};

		const createDraggingElement = (type: DraggingElementType, evt: React.MouseEvent<HTMLElement>) => {
			if (type === DraggingElementType.Image) {
				draggingElement.current = new Image(iconWidth, iconHeight);
			} else {
				draggingElement.current = document.createElement('p');
			}
			draggingElement.current.style.position = 'absolute';
			const [left, top] = getPositions(evt.clientX, evt.clientY);
			draggingElement.current.style.left = `${left}px`;
			draggingElement.current.style.top = `${top}px`;

			canvasContainerElement.current.addEventListener('mousemove', draggingHandler);
			canvasContainerElement.current.addEventListener('mouseup', dropHandler);
		};

		const onOkAddText = (evt: React.MouseEvent<HTMLElement>) => {
			if (currentTextObj) {
				batch(() => {
					setCurrentDraggingElementType(DraggingElementType.Paragraph);
					setIsTextWindowOpened(false);
				});
				const { bold, fontColor, fontSize, italic, lineThrough, text, underline } = currentTextObj;
				createDraggingElement(DraggingElementType.Paragraph, evt);
				draggingElement.current.innerHTML = text;
				draggingElement.current.style.background = 'none';
				draggingElement.current.style.color = fontColor;
				draggingElement.current.style.fontSize = fontSize.toString();
				if (bold) {
					draggingElement.current.style.fontWeight = 'bold';
				}
				if (italic) {
					draggingElement.current.style.fontStyle = 'italic';
				}
				if (lineThrough || underline) {
					draggingElement.current.style.textDecoration = `${lineThrough ? 'line-through' : ''}${underline ? ' underline' : ''}`;
				}
				canvasContainerElement.current.appendChild(draggingElement.current);
			}
		};

		const closeTextWindow = () => {
			batch(() => {
				setIsTextWindowOpened(false);
				setIsStickyNote(false);
				disableMenuOptions(false);
			});
		};

		const openTextWindow = (stickyNote: boolean) => {
			batch(() => {
				setIsStickyNote(stickyNote);
				setCanvasMainContainerCoordinates({
					x: canvasMainContainerElement.current.offsetWidth,
					y: canvasMainContainerElement.current.offsetHeight,
				});
				setIsTextWindowOpened(true);
				disableMenuOptions(true);
			});
		};

		const handleMenuActions = async (opt: MenuItems, evt?: React.MouseEvent<HTMLElement>): Promise<boolean> => {
			if (!disabledOpts) {
				switch (opt) {
					// FileOptions
					case MenuItems.Save:
						break;
					case MenuItems.NewFile:
						break;
					case MenuItems.ImportFile:
						break;
					case MenuItems.Directory:
						if (isMaps) {
							dispatch(setGraphicActionType(graphicActionType | GraphicActionType.ModalDirectory));
						} else {
							dispatch(setActionType(graphicActionType | GraphicActionType.ModalDirectory));
						}
						break;
					case MenuItems.ExportFile:
						break;
					case MenuItems.Undo:
						break;
					case MenuItems.Redo:
						break;
					case MenuItems.Copy:
						break;
					case MenuItems.Paste:
						break;
					case MenuItems.Cut:
						break;
					case MenuItems.Delete:
						addEscapeOptionEventListener();
						break;
					case MenuItems.PageSetup:
						break;
					// DrawingOptions
					case MenuItems.RedlineFreeHand:
						blockMenuItemsWithAddEscapeOptionEventListener();
						break;
					case MenuItems.RedlineRectangle:
						blockMenuItemsWithAddEscapeOptionEventListener();
						break;
					case MenuItems.RedlineFilledRectangle:
						blockMenuItemsWithAddEscapeOptionEventListener();
						break;
					case MenuItems.RedlineCircle:
						blockMenuItemsWithAddEscapeOptionEventListener();
						break;
					case MenuItems.RedlineFilledCircle:
						blockMenuItemsWithAddEscapeOptionEventListener();
						break;
					case MenuItems.RedlineArrow:
						blockMenuItemsWithAddEscapeOptionEventListener();
						break;
					case MenuItems.DeleteRedline:
						addEscapeOptionEventListener();
						break;
					case MenuItems.MonitorZone:
						break;
					case MenuItems.Pin:
						setCurrentDraggingElementType(DraggingElementType.Image);
						createDraggingElement(DraggingElementType.Image, evt);
						(draggingElement.current as HTMLImageElement).src = `${allStatesImageDirectory}/Pin.svg?_=${getScriptVersion()}`;
						canvasContainerElement.current.appendChild(draggingElement.current);
						break;
					case MenuItems.LineWeight:
						/* blockMenuItemsWithAddEscapeOptionEventListener(); */
						break;
					case MenuItems.Save:
						break;
					case MenuItems.LineSelectColorConfig:
						break;
					case MenuItems.FillSelectColorConfig:
						break;
					// SelectionOptions
					case MenuItems.Select:
						/* TODO: What we're gonna do with this opt?? */
						break;
					case MenuItems.Move:
						/* blockMenuItemsWithAddEscapeOptionEventListener(); */
						break;
					case MenuItems.Group:
						break;
					case MenuItems.Ungroup:
						break;
					case MenuItems.Rotate:
						blockMenuItemsWithAddEscapeOptionEventListener();
						break;
					case MenuItems.Resize:
						blockMenuItemsWithAddEscapeOptionEventListener();
						break;
					case MenuItems.FlipVertical:
						break;
					case MenuItems.FlipHorizontal:
						break;
					case MenuItems.BringToFront:
						break;
					case MenuItems.SendToBack:
						break;
					case MenuItems.BringForward:
						break;
					case MenuItems.SendBackward:
						break;
					// TextOptions
					case MenuItems.AddText:
						openTextWindow(false);
						break;
					case MenuItems.AddParagraph:
						openTextWindow(true);
						break;
					case MenuItems.Bold:
						break;
					case MenuItems.Italic:
						break;
					case MenuItems.Underline:
						break;
					case MenuItems.AlignLeft:
						break;
					case MenuItems.AlignCenter:
						break;
					case MenuItems.AlignRight:
						break;
					case MenuItems.FontColor:
						break;
					case MenuItems.SearchText:
						blockMenuItemsWithAddEscapeOptionEventListener();
						break;
					// ZoomBarOptions
					case MenuItems.ZoomIn:
						break;
					case MenuItems.ZoomOut:
						break;
					case MenuItems.ExtendZoom:
						break;
					//ViewOptions
					case MenuItems.ViewDevices:
						dispatch(switchFloatingWindowVisibilityAction({ devices: !isDevicesOpened }));
						break;
					case MenuItems.ViewAlarmQueue:
						dispatch(switchFloatingWindowVisibilityAction({ alarmQueue: !isAlarmQueueOpened }));
						break;
					case MenuItems.ViewPlottedDevices:
						dispatch(switchFloatingWindowVisibilityAction({ plottedDevices: !isPlottedDeviceOpened }));
						break;
					case MenuItems.ViewObjectProperties:
						dispatch(switchFloatingWindowVisibilityAction({ objectProperties: !isObjectPropertiesOpened }));
						break;
					case MenuItems.ViewBirdEye:
						dispatch(switchFloatingWindowVisibilityAction({ birdEye: !isBirdEyeOpened }));
						break;
					case MenuItems.ViewQuickAccess:
						dispatch(switchFloatingWindowVisibilityAction({ quickAccess: !isQuickAccessOpened }));
						break;
					default:
						break;
				}
			}

			return disabledOpts;
		};

		return (
			<>
				{loadTextWindow && (
					<DraggableWindow
						header={<label>{isStickyNote ? _('AddParagraph') : _('AddText')}</label>}
						id={isStickyNote ? 'TextWindow' : 'StickyNoteWindow'}
						initialCoordinates={{ x: 100, y: 100 }}
						key={'text'}
						onClose={() => closeTextWindow()}
						parentContainerId={canvasMainContainerId}
						parentCoordinates={canvasMainContainerCoordinates}
						footer={<TextWindowFooter onOkAddText={onOkAddText} />}
					>
						<TextWindow setTextObject={setCurrentTextObj} />
					</DraggableWindow>
				)}
				<WrappedComponent {...(props as T)} disabled={disabledOpts} handleMenuActions={handleMenuActions} />
			</>
		);
	};

	ComponentWithMenuActions.displayName = `withMenuActions(${displayName})`;

	return ComponentWithMenuActions;
};

export { withMenuActions };
